/**
 * Shared helpers for the Activity Signals feature — detecting and reading the
 * activity `update_loading_message` payload kinds the backend streams during a
 * V3 turn (thinking / tool execution / web-search query / web-search results).
 *
 * Contract: a status row is `message_type === "update_loading_message"` and
 * carries exactly one non-null nested payload. Only these activity kinds are
 * persisted and rendered as activity lines; the four legacy payloads
 * (web_search / issue_spotting / doc_generation / product_info) and the
 * `loading_message_type` animation hint are NOT activity signals.
 *
 * Inner payload shapes (all confirmed against live frames / backend models):
 *   thinking_payload            -> { message: string }
 *   tool_executing_payload      -> { tool_name: string }
 *   web_search_query_payload    -> { query: string }
 *   web_search_results_payload  -> { sources: { text, url }[] }
 * Parsing still falls back across alternate keys so the FE tolerates contract drift.
 */

export const ACTIVITY_MESSAGE_TYPE = "update_loading_message";

export type ActivityKind = "thinking" | "tool" | "query" | "sources";

/** Backend tool identifiers received in tool_executing_payload. */
export const TOOL_NAMES = {
  GET_DOCUMENT: "get_document",
  LIST_DOCUMENTS: "list_documents",
  GENERATE_DOCUMENT: "generate_document",
} as const;

/** Maps an activity kind to its nested payload field name. */
export const ACTIVITY_PAYLOAD_KEY: Record<ActivityKind, string> = {
  thinking: "thinking_payload",
  tool: "tool_executing_payload",
  query: "web_search_query_payload",
  sources: "web_search_results_payload",
};

export interface ActivitySource {
  text: string; // page title
  url: string; // source url
}

export interface ActivityMessage {
  id: string;
  trigger_id?: string | null;
  message_type?: string;
  created_at?: string;
  payload?: Record<string, any> | null;
  [key: string]: any;
}

/** Returns the activity kind of a message, or null if it is not a known activity frame. */
export function getActivityKind(message: ActivityMessage | null | undefined): ActivityKind | null {
  if (!message || message.message_type !== ACTIVITY_MESSAGE_TYPE) return null;
  const payload = message.payload;
  if (!payload) return null;
  if (payload.thinking_payload != null) return "thinking";
  if (payload.tool_executing_payload != null) return "tool";
  if (payload.web_search_query_payload != null) return "query";
  if (payload.web_search_results_payload != null) return "sources";
  return null;
}

/** True for activity-signal kinds only (not legacy loading messages). */
export function isActivityMessage(message: ActivityMessage | null | undefined): boolean {
  return getActivityKind(message) !== null;
}

export const PENDING_ACTIVITY_ROW_ID = "pending-analyzing";

/**
 * Client-side placeholder run rendered while a turn is running but the backend
 * has not streamed its first activity frame yet — that gap regularly ran 10-20s
 * with no status text at all, which reads as a hung chat (INH-4696).
 *
 * It is a `thinking` row carrying no reasoning text, so <ActivitySignals> renders
 * exactly the label-only "Analyzing" line the first real thinking frame renders.
 * When that frame lands, the real run replaces this one with no visual jump.
 * Never dispatched into `threadData.messages` — render-only.
 */
export const PENDING_ACTIVITY_ROWS: ActivityMessage[] = [
  {
    id: PENDING_ACTIVITY_ROW_ID,
    message_type: ACTIVITY_MESSAGE_TYPE,
    trigger_id: null,
    payload: { thinking_payload: { message: "" } },
  },
];

/** Reasoning text for a thinking frame. */
export function getThinkingText(message: ActivityMessage): string {
  const p = message?.payload?.thinking_payload;
  if (p == null) return "";
  if (typeof p === "string") return p;
  return p.message ?? p.text ?? "";
}

/** Backend tool identifier for a tool-execution frame. */
export function getToolName(message: ActivityMessage): string {
  const p = message?.payload?.tool_executing_payload;
  if (p == null) return "";
  if (typeof p === "string") return p;
  return p.tool_name ?? p.name ?? "";
}

/** The literal query for a web-search query frame. */
export function getQueryText(message: ActivityMessage): string {
  const p = message?.payload?.web_search_query_payload;
  if (p == null) return "";
  if (typeof p === "string") return p;
  // Confirmed backend shape: LoadingWebSearchQueryPayload { query: str }.
  return p.query ?? p.message ?? p.text ?? "";
}

/** The source list for a web-search results frame (may be empty). */
export function getSources(message: ActivityMessage): ActivitySource[] {
  const p = message?.payload?.web_search_results_payload;
  if (p == null) return [];
  // Confirmed live shape: { sources: [{ text, url }] }. Fall back across the other
  // candidate keys / a bare array so we tolerate contract drift.
  const list = Array.isArray(p) ? p : (p.sources ?? p.message ?? p.results ?? []);
  if (!Array.isArray(list)) return [];
  return list.filter((s: any) => s && (s.url || s.text)).map((s: any) => ({ text: s.text ?? "", url: s.url ?? "" }));
}
